import { SERVER_ORIGIN } from '../config';

const VISITOR_ID_KEY = 'gs_visitor_id';
const AUTH_STORAGE_KEY = 'gs_auth';

function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Long-lived visitor ID (persists across tabs/sessions)
let visitorId = localStorage.getItem(VISITOR_ID_KEY);
if (!visitorId) {
  visitorId = uuidv4();
  localStorage.setItem(VISITOR_ID_KEY, visitorId);
}

// Session-level ID (unique to this tab/instance)
let sessionId = sessionStorage.getItem('gs_session_id');
if (!sessionId) {
  sessionId = uuidv4();
  sessionStorage.setItem('gs_session_id', sessionId);
}

function getAuthHeader(): string | undefined {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed?.token ? `Bearer ${parsed.token}` : undefined;
  } catch {
    return undefined;
  }
}

let globalTelemetryEnabled = true;

export function trackEvent(eventName: string, payload?: any): void {
  if (!globalTelemetryEnabled) return;

  const authHeader = getAuthHeader();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }

  fetch(`${SERVER_ORIGIN}/custom/telemetry/event`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      eventName,
      visitorId,
      sessionId,
      payload,
    }),
  }).catch((err) => {
    // Fail silently so telemetry never degrades gameplay/UX
    console.warn('[telemetry] Event tracking failed:', err);
  });
}

export function trackError(message: string, stack?: string, url?: string): void {
  if (!globalTelemetryEnabled) return;

  const authHeader = getAuthHeader();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }

  fetch(`${SERVER_ORIGIN}/custom/telemetry/error`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      message,
      stack,
      url: url || window.location.href,
      userAgent: navigator.userAgent,
    }),
  }).catch((err) => {
    // Fail silently
    console.warn('[telemetry] Error tracking failed:', err);
  });
}

let isInitialized = false;

export function initTelemetry(): void {
  if (isInitialized) return;
  isInitialized = true;

  fetch(`${SERVER_ORIGIN}/custom/telemetry/config`)
    .then((res) => res.json())
    .then((data: any) => {
      globalTelemetryEnabled = data.enabled ?? true;
      if (globalTelemetryEnabled) {
        // Track page landing
        trackEvent('funnel_landing');
      }
    })
    .catch((err) => {
      console.warn('[telemetry] Config fetch failed:', err);
    });

  // Capture uncaught JavaScript errors
  window.addEventListener('error', (event) => {
    // Guard against logging failures from telemetry endpoints themselves
    if (event.filename && event.filename.includes('/custom/telemetry/')) {
      return;
    }
    const message = event.message || 'Unknown error';
    const stack = event.error?.stack || undefined;
    const url = event.filename || window.location.href;
    trackError(message, stack, url);
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;
    trackError(`Unhandled Rejection: ${message}`, stack);
  });
}
